import { useState } from 'react';
import { Badge, Button, Csel, Icon, Section, useToast } from '@efira/ui';

import { useConnectLive, useDisconnectLive, usePreflight } from '../../api/queries';

/**
 * Симулятор эфира: мнимые зрители, комментарии и подарки без TikTok.
 *
 * ЭТО НЕ ДЕМО-РЕЖИМ, а рабочий инструмент. Проверить игру и привязки перед
 * эфиром, не подключая канал и не тратя дневную квоту, - главный ответ страху
 * новичка (SPEC 5.3). У эталона это лучшая часть продукта, и повторяется она
 * целиком.
 *
 * ПЯТЬ СТУПЕНЕЙ, как в эталоне. Верхняя - «Шторм» - это репетиция
 * прайм-тайма, а не служебный режим: поведение игры на пике стример обязан
 * увидеть ДО эфира, а не в эфире. Сокращение до трёх было отменено владельцем
 * 2026-09-19 вместе с его обоснованием - оно ссылалось на замер ЧУЖОГО
 * протокола (docs/DECISIONS.md, [2026-09-19] о пяти ступенях).
 */
const LEVELS = [
  { value: 'calm', label: 'Тихо', hint: 'десяток зрителей, удобно настраивать привязки' },
  { value: 'chill', label: 'Спокойно', hint: 'видно, что игра реагирует' },
  { value: 'active', label: 'Активно', hint: 'обычный рабочий эфир' },
  { value: 'hyped', label: 'Разогнанно', hint: 'событий вдвое больше обычного' },
  { value: 'chaos', label: 'Шторм', hint: 'пик прайм-тайма: проверка перед эфиром' },
];

export function Simulator() {
  const preflight = usePreflight();
  const connect = useConnectLive();
  const disconnect = useDisconnectLive();
  const toast = useToast();
  const [intensity, setIntensity] = useState('active');

  const connection = preflight.data?.connection;
  const running = connection?.status === 'live' && connection.sourceId === 'synthetic';

  return (
    /* СЕКЦИЯ, А НЕ СВОЙ ЗАГОЛОВОК. Симулятор был единственным блоком левой
       колонки без шеврона и без прописных - третий вид шапки на продукт
       (находка владельца 2026-09-25). */
    <Section
      title="Симулятор эфира"
      id="simulator"
      status={
        running ? (
          <Badge variant="secondary" data-left="sim-running">
            идёт
          </Badge>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2" data-left="simulator">
        <p className="text-foreground/80 text-sm">
          Мнимые зрители, комментарии и подарки. TikTok подключать не нужно, дневная квота не
          расходуется.
        </p>

        <label className="text-foreground/80 text-xs font-medium" htmlFor="sim-intensity">
          Интенсивность
        </label>
        <div data-left="sim-intensity">
          <Csel
            id="sim-intensity"
            value={intensity}
            onChange={setIntensity}
            options={LEVELS}
            disabled={running}
            triggerClassName="w-full"
          />
        </div>

        <Button
          /*
            СИМУЛЯТОР - ВТОРИЧНОЕ ДЕЙСТВИЕ, А НЕ ГЛАВНОЕ.

            Решение владельца 2026-09-26 (5) и находка аудита 31: на экране
            одна главная кнопка, и это та, что ведёт к эфиру. Симулятор
            показывает, как всё выглядит, - но зрителей он не приводит.
            Две залитые кнопки подряд заставляли выбирать между ними, и
            симулятор выигрывал: он единственный работал без аккаунта
            TikTok.

            Остановка - НЕ разрушающее действие (единица 21): она ничего не
            стирает и перезапускается тем же нажатием. Предупреждающий вид
            закреплён за списком разрушающих действий
            (`console/destructive.ts`), и красная кнопка, которую можно жать
            без последствий, учила бы не бояться красных.
          */
          variant="secondary"
          size="s"
          data-left="sim-start"
          className="w-full gap-2"
          disabled={connect.isPending || disconnect.isPending}
          onClick={() => {
            if (running) {
              disconnect.mutate(undefined, {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Симулятор остановлен' });
                },
              });
              return;
            }

            connect.mutate(
              { mode: 'synthetic', intensity },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Симулятор запущен' });
                },
                onError: (error) => {
                  // Отказ подключения объясняется словами: у каждой причины своё
                  // действие, и молчаливого отказа быть не должно.
                  const reason =
                    typeof error === 'object' && error !== null && 'message' in error
                      ? String((error as { message: unknown }).message)
                      : 'Симулятор не запустился. Попробуйте ещё раз.';
                  toast.show({ tone: 'warning', title: 'Не запустилось', text: reason });
                },
              },
            );
          }}
        >
          <Icon name={running ? 'stop' : 'play'} size="sm" />
          {running ? 'Остановить симулятор' : 'Запустить симулятор'}
        </Button>
      </div>
    </Section>
  );
}
